/**
 * assetMarkerGrouping — THE grouping of POIs into the system layers the Model Assets pane lists,
 * and the world point each asset flag is placed at. Pure, so both are testable without a viewer.
 *
 * WHY A SEPARATE MODULE. The panorama marker work spent a long day on bugs that were all in
 * decisions (which point, which key, which filter) rather than in rendering, and none of them
 * were visible from looking at the screen. The same decisions live here instead of inside a hook.
 *
 * WHICH COORDINATES. A POI carries two, and they are NOT interchangeable:
 *   - `coordinates` are the NORMALISED 2D coordinates (axis-flipped, relative to model bounds
 *     min) — what the minimap and 2D surfaces use. Placing a 3D flag there puts it nowhere near
 *     the element.
 *   - `model_coordinates` are the element's bounding-box centre in MODEL space, captured at
 *     extraction by RVTExtractorService.extractElementPosition — which is what a flag wants.
 * Both arrive as JSON strings from the database, so both need parsing before use.
 */

export interface AssetSystemLayer {
  /** Stable id for toggle state — the system category name, lowercased. */
  id: string;
  /** What the pane shows. */
  name: string;
  count: number;
  /** Used by the Assets heading to count each asset once across overlapping systems. */
  assetKeys?: string[];
}

// A UI layer, never a system assignment written to an asset.
export const UNASSIGNED_ASSET_LAYER = '__mabat_unassigned_assets__';

export interface AssetMarkerPoint {
  poi: any;
  systemId: string;
  /**
   * The POI's stored model-space point, when it has one. A FALLBACK only: flags are anchored on
   * the element's own geometry (dbId → fragment world bounds), because the stored coordinates
   * need a model→viewer transform that is easy to get wrong — the first version of this layer
   * ran them through the panorama normaliser and put every flag outside the building.
   */
  position: { x: number; y: number; z: number } | null;
}

/** POIs arrive with JSON-string coordinate columns; tolerate objects too. */
const parsePoint = (value: unknown): { x: number; y: number; z: number } | null => {
  if (!value) return null;
  let raw: any = value;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { return null; }
  }
  if (!raw || typeof raw !== 'object') return null;
  const x = Number(raw.x); const y = Number(raw.y); const z = Number(raw.z);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
  return { x, y, z };
};

const asSystem = (name: unknown): { id: string; name: string } | null => {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!trimmed) return null;
  return { id: trimmed.toLowerCase(), name: trimmed };
};

/**
 * The POI's system PATH, root first, from the legacy mirror.
 *
 * systemsRegistry writes the primary system's whole chain across two columns (migration 058
 * restored this after 053 had flattened it): system_category is the ROOT ancestor and
 * system_subcategory is a JSON array of the names beneath it. Reading both is what lets the
 * viewer know about ancestors without fetching the systems table.
 */
const pathOf = (poi: any): Array<{ id: string; name: string }> => {
  const root = asSystem(
    poi?.system_category ?? poi?.systemCategory ?? poi?.metadata?.systemCategory ?? poi?.custom_data?.systemCategory
  );
  if (!root) return [];
  const raw = poi?.system_subcategory ?? poi?.systemSubcategory;
  let below: unknown[] = [];
  if (Array.isArray(raw)) below = raw;
  else if (typeof raw === 'string' && raw.trim() !== '') {
    // The column holds a JSON array; a bare string is a pre-039 row and is one level.
    try {
      const parsed = JSON.parse(raw);
      below = Array.isArray(parsed) ? parsed : [raw];
    } catch { below = [raw]; }
  }
  return [root, ...below.map(asSystem).filter(Boolean) as Array<{ id: string; name: string }>];
};

/**
 * EVERY system a POI belongs to, INCLUDING its ancestors.
 *
 * MEMBERSHIP ROLLS UP (migration 058): an asset attached to SA-01 is also in Supply Air, so
 * toggling the parent layer must show it. The junction stores only the node the asset is
 * attached to — the deepest one — so the ancestors come from the path mirror. Without this a
 * parent layer would be empty while its children were full, which reads as a broken layer.
 *
 * `poi.systems` is the junction as the API returns it; when a POI genuinely belongs to several
 * systems those are unioned with the path.
 */
export const systemsOf = (poi: any): Array<{ id: string; name: string }> => {
  const byId = new Map<string, { id: string; name: string }>();
  const add = (system: { id: string; name: string } | null) => {
    // De-duplicate by folded id: "Electrical" and "electrical" are one system.
    if (system && !byId.has(system.id)) byId.set(system.id, system);
  };

  const junction = Array.isArray(poi?.systems)
    ? (poi.systems
        .map((entry: any) => asSystem(typeof entry === 'string' ? entry : entry?.name))
        .filter(Boolean) as Array<{ id: string; name: string }>)
    : [];
  for (const system of junction) add(system);

  const path = pathOf(poi);
  if (path.length === 0) return Array.from(byId.values());

  // THE MIRROR IS ONLY TRUSTED FOR ANCESTRY WHEN IT AGREES WITH THE JUNCTION.
  // systemsRegistry rewrites both together, so normally the path's LEAF is one of the
  // junction's systems and the levels above it are the ancestors the junction cannot express.
  // They can still disagree: the POI batch-update route accepts raw system_category strings
  // (the desync documented since migration 039). When they do, the junction is the truth and
  // the path is stale — unioning blindly would show a system the asset is no longer in.
  const leaf = path[path.length - 1];
  if (junction.length === 0 || byId.has(leaf.id)) {
    for (const system of path) add(system);
  }
  return Array.from(byId.values());
};

/** Just the deepest node the POI is attached to — no ancestors. */
export const leafSystemsOf = (poi: any): Array<{ id: string; name: string }> => {
  const fromJunction = Array.isArray(poi?.systems)
    ? (poi.systems
        .map((entry: any) => asSystem(typeof entry === 'string' ? entry : entry?.name))
        .filter(Boolean) as Array<{ id: string; name: string }>)
    : [];
  if (fromJunction.length > 0) {
    const byId = new Map<string, { id: string; name: string }>();
    for (const system of fromJunction) if (!byId.has(system.id)) byId.set(system.id, system);
    return Array.from(byId.values());
  }
  const path = pathOf(poi);
  return path.length ? [path[path.length - 1]] : [];
};

/** The POI's PRIMARY system — the first by name, matching the derived legacy column. */
export const systemOf = (poi: any): { id: string; name: string } | null => {
  const systems = systemsOf(poi);
  if (systems.length === 0) return null;
  return [...systems].sort((a, b) => a.name.localeCompare(b.name))[0];
};

/** Layer membership includes unassigned assets without inventing a real system for them. */
export const assetLayersOf = (poi: any): Array<{ id: string; name: string }> => {
  const systems = systemsOf(poi);
  return systems.length ? systems : [{ id: UNASSIGNED_ASSET_LAYER, name: 'Unassigned' }];
};

export const isAssetLayerEnabled = (poi: any, enabled: Set<string>): boolean =>
  assetLayersOf(poi).some(layer => enabled.has(layer.id));

/** One row per system plus an Unassigned row. Every asset remains reachable. */
export const buildAssetSystemLayers = (pois: any[]): AssetSystemLayer[] => {
  const bySystem = new Map<string, AssetSystemLayer>();
  for (const [index, poi] of (pois || []).entries()) {
    // Counted under EVERY system it belongs to — the layer's count is "assets in this system",
    // so an asset in two systems is one asset in each, not half an asset in each.
    const key = String(poi.id ?? `row-${index}`);
    for (const system of assetLayersOf(poi)) {
      const existing = bySystem.get(system.id);
      if (existing) { existing.count += 1; existing.assetKeys!.push(key); }
      else bySystem.set(system.id, { id: system.id, name: system.name, count: 1, assetKeys: [key] });
    }
  }
  return Array.from(bySystem.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * The POIs that should currently carry a flag. `enabledSystems` is a whitelist of system ids;
 * an empty set means no flags.
 *
 * Missing or unparseable coordinates do NOT exclude a POI: the anchor comes from geometry, so an
 * asset with no stored point still gets a flag as long as its element resolves.
 */
export const buildAssetMarkerPoints = (
  pois: any[],
  enabledSystems: Set<string>
): AssetMarkerPoint[] => {
  if (!enabledSystems || enabledSystems.size === 0) return [];
  const points: AssetMarkerPoint[] = [];
  for (const poi of pois || []) {
    const systems = assetLayersOf(poi);
    // ONE FLAG PER ASSET, not one per system. An asset in two enabled systems would otherwise
    // get two identical flags stacked at the same point — unclickable and twice the geometry.
    const matched = systems.filter((system) => enabledSystems.has(system.id));
    if (matched.length === 0) continue;
    // Its colour comes from the first matching system by name, so the flag looks the same
    // whichever order the layers were switched on.
    const primary = [...matched].sort((a, b) => a.name.localeCompare(b.name))[0];
    // model_coordinates ONLY — see the header note on why `coordinates` is the wrong one.
    points.push({ poi, position: parsePoint(poi.model_coordinates), systemId: primary.id });
  }
  return points;
};
